/**
 * Cryptography Module for Ottu JS SDK v3
 * 
 * Handles all cryptographic operations including RSA-OAEP encryption
 * for Card Holder Data (CHD) protection. Uses secure defaults and
 * validates key parameters for PCI-DSS compliance.
 */

/**
 * Validates RSA key size from PEM data before import
 * @param {Uint8Array} binaryKey - The binary key data
 * @returns {number} The estimated key size in bits
 * @throws {Error} If key size is below minimum requirements
 */
function validateKeySize(binaryKey) {
  try {
    // For RSA keys in SPKI format, the key size can be estimated from the total length
    // This is an approximation but sufficient for security validation
    const keyLength = binaryKey.length;
    let estimatedKeySize;
    
    // Common RSA key sizes and their approximate SPKI DER lengths
    if (keyLength >= 290 && keyLength <= 320) {
      estimatedKeySize = 2048;
    } else if (keyLength >= 360 && keyLength <= 400) {
      estimatedKeySize = 3072; 
    } else if (keyLength >= 540 && keyLength <= 580) {
      estimatedKeySize = 4096;
    } else if (keyLength < 200) {
      estimatedKeySize = 1024; // Likely 1024-bit or smaller
    } else {
      // For other sizes, estimate based on length (rough approximation)
      estimatedKeySize = Math.round(keyLength * 8 / 1.2); // Rough estimate
    }
    
    const MIN_KEY_SIZE = 2048;
    if (estimatedKeySize < MIN_KEY_SIZE) {
      throw new Error(`RSA key size ${estimatedKeySize} bits is below minimum required ${MIN_KEY_SIZE} bits for secure CHD encryption`);
    }
    
    return estimatedKeySize;
  } catch (error) {
    if (error.message.includes('key size')) {
      throw error; // Re-throw validation errors
    }
    throw new Error(`Failed to validate RSA key size: ${error.message}`);
  }
}

/**
 * Imports RSA public key for encryption with security validation
 * @param {string} pem - PEM-encoded RSA public key
 * @param {string} hashAlgorithm - Hash algorithm (default: SHA-256)
 * @returns {Promise<CryptoKey>} The imported crypto key
 * @throws {Error} If key is invalid, too small, or import fails
 */
async function importPublicKey(pem, hashAlgorithm = 'SHA-256') {
  if (!pem || typeof pem !== 'string') {
    throw new Error('Invalid PEM key: must be a non-empty string');
  }
  
  if (!['SHA-1', 'SHA-256', 'SHA-384', 'SHA-512'].includes(hashAlgorithm)) {
    throw new Error(`Unsupported hash algorithm: ${hashAlgorithm}. Use SHA-256 or stronger.`);
  }
  
  // Warn about deprecated SHA-1 usage
  if (hashAlgorithm === 'SHA-1') {
    console.warn('[Security] SHA-1 is deprecated for RSA-OAEP. Consider upgrading to SHA-256.');
  }
  
  // Remove PEM header/footer and extract base64 content
  const keyContent = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/[\r\n\s]/g, '');
  
  if (!keyContent || keyContent.length === 0) {
    throw new Error('Invalid PEM format: no key content found');
  }
  
  try {
    // Decode base64 to binary
    const binaryKey = Uint8Array.from(atob(keyContent), (c) => c.charCodeAt(0));
    
    // Validate key size before import (since imported key will be non-extractable)
    validateKeySize(binaryKey);
    
    // Import the RSA public key
    const cryptoKey = await window.crypto.subtle.importKey(
      'spki', // SubjectPublicKeyInfo format
      binaryKey.buffer,
      {
        name: 'RSA-OAEP',
        hash: { name: hashAlgorithm },
      },
      false, // Not extractable for security
      ['encrypt']
    );
    
    return cryptoKey;
  } catch (error) {
    if (error.name === 'DataError' || error.name === 'InvalidAccessError') {
      throw new Error('Invalid RSA public key format or corrupted key data');
    }
    if (error.message.includes('key size')) {
      throw error; // Re-throw key size validation errors
    }
    throw new Error(`RSA key import failed: ${error.message}`);
  }
}

/**
 * Encrypts Card Holder Data (CHD) using RSA-OAEP with secure defaults
 * @param {string} publicKeyPem - PEM-encoded RSA public key
 * @param {string} chd - Card Holder Data to encrypt
 * @param {string} hashAlgorithm - Hash algorithm (default: SHA-256)
 * @returns {Promise<string>} Base64-encoded encrypted data
 * @throws {Error} If encryption fails or parameters are invalid
 */
async function encryptCHD(publicKeyPem, chd, hashAlgorithm = 'SHA-256') {
  if (!chd || typeof chd !== 'string') {
    throw new Error('Invalid CHD: must be a non-empty string');
  }
  
  if (chd.length > 245) { // RSA-OAEP with SHA-256 max payload for 2048-bit key
    throw new Error('CHD payload too large for RSA-OAEP encryption');
  }
  
  try {
    // Import and validate the public key
    const publicKey = await importPublicKey(publicKeyPem, hashAlgorithm);
    
    // Encode CHD as UTF-8 bytes
    const encodedCHD = new TextEncoder().encode(chd);
    
    // Encrypt using RSA-OAEP
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
        // hash is already specified in the key during import
      },
      publicKey,
      encodedCHD
    );
    
    // Convert encrypted data to base64
    const encryptedArray = new Uint8Array(encryptedBuffer);
    const encryptedCHD = btoa(String.fromCharCode(...encryptedArray));
    
    return encryptedCHD;
  } catch (error) {
    // Don't log the actual CHD in error messages for security
    console.error('CHD encryption failed:', {
      error: error.message,
      hashAlgorithm,
      chdLength: chd ? chd.length : 0
    });
    throw new Error(`CHD encryption failed: ${error.message}`);
  }
}

/**
 * Legacy function for backward compatibility with SHA-1
 * @deprecated Use encryptCHD with SHA-256 instead
 * @param {string} publicKeyPem - PEM-encoded RSA public key
 * @param {string} chd - Card Holder Data to encrypt
 * @returns {Promise<string>} Base64-encoded encrypted data
 */
async function encryptCHDLegacy(publicKeyPem, chd) {
  console.warn('[Deprecation] encryptCHDLegacy uses SHA-1. Upgrade to encryptCHD with SHA-256.');
  return encryptCHD(publicKeyPem, chd, 'SHA-1');
}

/**
 * Generate a cryptographically secure random string for nonces/tokens
 * @param {number} length - Length of the random string
 * @returns {string} Base64-encoded random string
 */
function generateSecureRandom(length = 32) {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array));
}

module.exports = {
  importPublicKey,
  encryptCHD,
  encryptCHDLegacy,
  validateKeySize,
  generateSecureRandom
};